import Broadcast, { BroadcastTemplate } from "@/types/Broadcast.interface"
import { BroadcastParams } from "@/types/PaginationParams.interface"
import Group from "@/types/Group.interface"
import Message from "@/types/Message.interface"
import { MessageParams } from "@/types/PaginationParams.interface"
import Pagination from "@/types/Pagination.interface"

interface State {
    broadcastsViewBroadcasts: Broadcast[]
    broadcastsViewBroadcastsCurrentPage: number
    broadcastsViewBroadcastsLastPage: number
    broadcastsViewBroadcastsNextPage: number
    broadcastsViewBroadcastsParams: BroadcastParams
    broadcastsViewBroadcastsParamsDefault: BroadcastParams
    broadcastsViewGroups: Group[]
    broadcastsViewFailedMessages: Message[]
    broadcastsViewFailedMessagesCurrentPage: number
    broadcastsViewFailedMessagesLastPage: number
    broadcastsViewFailedMessagesNextPage: number
    broadcastsViewFailedMessagesPaginating: boolean
    broadcastsViewFailedMessagesParams: MessageParams
    broadcastsViewFailedMessagesTotal: number
    broadcastsViewSentMessages: Message[]
    broadcastsViewSentMessagesCurrentPage: number
    broadcastsViewSentMessagesLastPage: number
    broadcastsViewSentMessagesNextPage: number
    broadcastsViewSentMessagesPaginating: boolean
    broadcastsViewSentMessagesParams: MessageParams
    broadcastsViewSentMessagesTotal: number
    broadcastsViewTotalMessages: number
    broadcastsViewTotalSendingMessages: number
    broadcastsViewPaginating: boolean
    broadcastsViewSelectedBroadcast: Broadcast
    broadcastsViewSelectedGroups: Group[]
    broadcastsViewSelectedBroadcastTotalCount: number
    broadcastsViewSelectedBroadcastLoadingCount: boolean
    broadcastsViewSelectedTemplate: BroadcastTemplate
    broadcastsViewMediaHint: boolean
}

const defaults = () => {
    return {
        broadcastsViewBroadcasts: [],
        broadcastsViewBroadcastsCurrentPage: null,
        broadcastsViewBroadcastsLastPage: null,
        broadcastsViewBroadcastsNextPage: null,
        broadcastsViewBroadcastsParams: { page: 1, type: 'all' },
        broadcastsViewBroadcastsParamsDefault: { page: 1, type: 'all' },
        broadcastsViewGroups: [],
        broadcastsViewFailedMessages: [],
        broadcastsViewFailedMessagesCurrentPage: null,
        broadcastsViewFailedMessagesLastPage: null,
        broadcastsViewFailedMessagesNextPage: null,
        broadcastsViewFailedMessagesPaginating: false,
        broadcastsViewFailedMessagesParams: { page: 1 },
        broadcastsViewFailedMessagesTotal: 0,
        broadcastsViewSentMessages: [],
        broadcastsViewSentMessagesCurrentPage: null,
        broadcastsViewSentMessagesLastPage: null,
        broadcastsViewSentMessagesNextPage: null,
        broadcastsViewSentMessagesPaginating: false,
        broadcastsViewSentMessagesParams: { page: 1 },
        broadcastsViewSentMessagesTotal: 0,
        broadcastsViewTotalMessages: 0,
        broadcastsViewTotalSendingMessages: 0,
        broadcastsViewPaginating: false,
        broadcastsViewSelectedBroadcast: null,
        broadcastsViewSelectedGroups: null,
        broadcastsViewSelectedBroadcastTotalCount: 0,
        broadcastsViewSelectedBroadcastLoadingCount: false,
        broadcastsViewSelectedTemplate: null,
        broadcastsViewMediaHint: false
    }
}

export default {
    state: defaults(),
    mutations: {
        broadcastsViewBroadcastsParamsSet(state: State, params: BroadcastParams): void {
            state.broadcastsViewBroadcastsParams = params
        },
        broadcastsViewBroadcastsPush(state: State, broadcasts: Broadcast[]): void {
            //  Update our state
            state.broadcastsViewBroadcasts = window.removeDuplicatesById(state.broadcastsViewBroadcasts, broadcasts)

            //  Get the currently selected broadcast
            const selectedBroadcast = state.broadcastsViewSelectedBroadcast

            //  If the broadcast does not exist, we have nothing to do
            if (!selectedBroadcast) return

            //  Else, find the selected broadcast in the broadcasts list
            const updatedBroadcast = broadcasts.find((x: Broadcast) => x.id == selectedBroadcast.id)

            //  If the selected broadcast was NOT updated, we have nothing to do, just return
            if (!updatedBroadcast) return

            //  Else, update the selected broadcast
            Object.assign(state.broadcastsViewSelectedBroadcast, updatedBroadcast)
        },
        broadcastsViewBroadcastsRemove(state: State, ids: number[]): void {
            //  Get our current list of broadcasts
            const broadcasts = state.broadcastsViewBroadcasts

            //  If no broadcasts, do nothing
            if (!broadcasts) return

            //  Else, filter out our removed ids
            const filtered = broadcasts.filter((x: Broadcast) => !ids.includes(x.id))

            //  Set our broadcasts to the filtered list
            state.broadcastsViewBroadcasts = filtered

            //  Get the currently selected broadcast
            const selectedBroadcast = state.broadcastsViewSelectedBroadcast

            //  If the broadcast does not exist, we're done so do nothing
            if (!selectedBroadcast) return

            //  Else, check if it was deleted
            const deleted = ids.find((x: number) => x == selectedBroadcast.id)

            //  If the selected broadcast was NOT found, then do nothing
            if (!deleted) return

            //  Else, set our selected broadcast to null
            state.broadcastsViewSelectedBroadcast = null
        },
        broadcastsViewBroadcastsSync(state: State, updates: Broadcast[]): void {
            //  Get our list of broadcasts
            const broadcasts = state.broadcastsViewBroadcasts

            //  If no broadcasts, do nothing
            if (!broadcasts) return

            //  Get our broadcasts params
            const params = state.broadcastsViewBroadcastsParams

            //  Apply our filtering
            const filtered = updates.appSyncFilter(params, 'broadcasts')

            //  Find the items we will have to remove because of the filter
            const remove = window._.differenceBy(updates, filtered, 'id')

            //  Update our list with the filtered array and remove the unneeded broadcasts
            state.broadcastsViewBroadcasts = window.removeDuplicatesById(state.broadcastsViewBroadcasts, filtered, remove)

            //  Sort the state (this sorts by status THEN id fucking sick i know )
            state.broadcastsViewBroadcasts.sort((a: Broadcast, b: Broadcast) => {
                //  @ts-ignore
                return +window.getDate(b.updated_at) - +window.getDate(a.updated_at)
            })

            //  Check if we have a selected broadcast
            const selected = state.broadcastsViewSelectedBroadcast

            //  If it doesn't exist, do nothing
            if (!selected) return

            console.log("SELECTED AND UPDATES", selected, updates)

            //  Else, check if our filtered contains this ids
            const exists = updates.find((x: Broadcast) => x.id == selected.id)

            //  If it does NOT contain the id, do nothing
            if (!exists) return

            //  We update the properties that changed, so we don't remove existing ones
            const updated = Object.assign({}, state.broadcastsViewSelectedBroadcast, exists)

            //  Else, update our selected broadcast
            state.broadcastsViewSelectedBroadcast = updated
        },
        broadcastsViewGroupsPush(state: State, updates: Group[]): void {
            //  Get our list of groups
            const groups = state.broadcastsViewGroups

            //  If no groups, do nothing
            if (!groups) return

            //  Grab all the ids from our groups
            const ids = groups.map((x: Group) => x.id)

            //  Else, filter out our updates to ONLY contain items that are in the persons list
            const filtered = updates.filter((x: Group) => ids.includes(x.id))

            //  Update our list with the filtered array
            state.broadcastsViewGroups = window.removeDuplicatesById(state.broadcastsViewGroups, filtered)

            //  Check if we have selected groups
            const selected = state.broadcastsViewSelectedGroups

            //  If it doesn't exist, do nothing
            if (!selected) return

            //  Else, update our selected group
            state.broadcastsViewSelectedGroups = window.removeDuplicatesById(state.broadcastsViewSelectedGroups, filtered)
        },
        broadcastsViewGroupsRemove(state: State, ids: number[]): void {
            //  Get our current lists of groups
            const groups = state.broadcastsViewGroups

            //  If no groups, do nothing
            if (!groups) return

            //  Else, filter out our removed ids
            const filtered = groups.filter((x: Group) => !ids.includes(x.id))

            //  Set our groups to the filtered list
            state.broadcastsViewGroups = filtered

            //  Get the currently selected groups
            const selected = state.broadcastsViewSelectedGroups

            //  If the groups do NOT exist, we have nothing to do
            if (!selected) return

            //  Else, check if any of these groups were deleted
            const existing = selected.filter((x: Group) => !ids.includes(x.id))

            //  Update our selected groups
            state.broadcastsViewSelectedGroups = existing
        },
        broadcastsViewGroupsSet(state: State, groups: Group[]): void {
            state.broadcastsViewGroups = groups
        },
        //  FAILED MESSAGES
        broadcastsViewFailedMessagesParamsReset(state: State): void {
            state.broadcastsViewFailedMessagesParams.page = 1
        },
        broadcastsViewFailedMessagesParamsSet(state: State, params: MessageParams): void {
            state.broadcastsViewFailedMessagesParams = params
        },
        broadcastsViewFailedMessagesPush(state: State, messages: Message[]): void {
            state.broadcastsViewFailedMessages = window.removeDuplicatesById(state.broadcastsViewFailedMessages, messages)
        },
        broadcastsViewFailedMessagesPaginatingSet(state: State, bool: boolean): void {
            state.broadcastsViewFailedMessagesPaginating = bool
        },
        broadcastsViewFailedMessagesPaginationPush(state: State, page: Pagination): void {
            //  If this is page one, reset the messages list
            if (page.current_page === 1) state.broadcastsViewFailedMessages = []

            //  Set our pages
            state.broadcastsViewFailedMessagesCurrentPage = page.current_page
            state.broadcastsViewFailedMessagesLastPage = page.last_page
            state.broadcastsViewFailedMessagesNextPage = page.current_page + 1

            //  Set our total
            state.broadcastsViewFailedMessagesTotal = page.total
        },
        broadcastsViewFailedMessagesSync(state: State, messages: Message[]): void {
            //  Check if we currently have a selected broadcast
            const broadcast = state.broadcastsViewSelectedBroadcast

            //  If we don't, do nothing
            if (!broadcast) return

            //  Else, filter out our messages to only contain ones matching this broadcast id
            const filtered = messages.filter((x: Message) => x.broadcast_id === broadcast.id)

            //  If we don't have any, do nothing
            if (!filtered.length) return

            //  Else, grab only the messages with a failed status
            const filteredFail = filtered.filter((x: Message) => x.status === 'failed' && x.standard_err != '10')

            //  Grab our failed messages
            const failed = state.broadcastsViewFailedMessages

            //  Find how many messages we have in filtered that are NOT in filtered
            //  This gets us the messages we have to remove since they no longer match our filter
            const remove = window._.differenceBy(filtered, filteredFail, 'id')

            //  Add these messages to the list and removed the non-matches
            state.broadcastsViewFailedMessages = window.removeDuplicatesById(failed, filteredFail, remove)
        },
        broadcastsViewFailedMessagesTotalSet(state: State, total: number): void {
            //  Update our total
            state.broadcastsViewFailedMessagesTotal = total
        },
        //  SENT MESSAGES
        broadcastsViewSentMessagesParamsReset(state: State): void {
            state.broadcastsViewSentMessagesParams.page = 1
        },
        broadcastsViewSentMessagesParamsSet(state: State, params: MessageParams): void {
            state.broadcastsViewSentMessagesParams = params
        },
        broadcastsViewSentMessagesPush(state: State, messages: Message[]): void {
            state.broadcastsViewSentMessages = window.removeDuplicatesById(state.broadcastsViewSentMessages, messages)
        },
        broadcastsViewSentMessagesPaginatingSet(state: State, bool: boolean): void {
            state.broadcastsViewSentMessagesPaginating = bool
        },
        broadcastsViewSentMessagesPaginationPush(state: State, page: Pagination): void {
            //  If this is page one, reset the messages list
            if (page.current_page === 1) state.broadcastsViewSentMessages = []

            //  Set our pages
            state.broadcastsViewSentMessagesCurrentPage = page.current_page
            state.broadcastsViewSentMessagesLastPage = page.last_page
            state.broadcastsViewSentMessagesNextPage = page.current_page + 1

            //  Set our total
            state.broadcastsViewSentMessagesTotal = page.total
        },
        broadcastsViewSentMessagesSync(state: State, messages: Message[]): void {
            //  Check if we currently have a selected broadcast
            const broadcast = state.broadcastsViewSelectedBroadcast

            //  If we don't, do nothing
            if (!broadcast) return

            //  Else, filter out our messages to only contain ones matching this broadcast id
            const filtered = messages.filter((x: Message) => x.broadcast_id === broadcast.id)

            //  If we don't have any, do nothing
            if (!filtered.length) return

            //  Else, grab only the messages without a failed status
            const filteredSent = filtered.filter((x: Message) => x.status !== 'failed')

            //  Grab our sent messages
            const sent = state.broadcastsViewSentMessages

            //  Find how many messages we have in filtered that are NOT in filtered
            //  This gets us the messages we have to remove since they no longer match our filter
            const remove = window._.differenceBy(filtered, filteredSent, 'id')

            //  Add these messages to the list and removed the non-matches
            state.broadcastsViewSentMessages = window.removeDuplicatesById(sent, filteredSent, remove)
        },
        broadcastsViewSentMessagesTotalSet(state: State, total: number): void {
            //  Update our total
            state.broadcastsViewSentMessagesTotal = total
        },
        broadcastsViewTotalMessagesSet(state: State, total: number): void {
            state.broadcastsViewTotalMessages = total
        },
        broadcastsViewTotalSendingMessagesSet(state: State, total: number): void {
            state.broadcastsViewTotalSendingMessages = total
        },
        broadcastsViewMessagesReset(state: State): void {
            state.broadcastsViewFailedMessages = []
            state.broadcastsViewSentMessages = []
        },
        //  BROADCASTS
        broadcastsViewPaginatingSet(state: State, bool: boolean): void {
            state.broadcastsViewPaginating = bool
        },
        broadcastsViewPaginationPush(state: State, page: Pagination): void {
            //  If this is page one, reset the broadcasts list
            if (page.current_page === 1) state.broadcastsViewBroadcasts = []

            state.broadcastsViewBroadcastsCurrentPage = page.current_page
            state.broadcastsViewBroadcastsLastPage = page.last_page
            state.broadcastsViewBroadcastsNextPage = page.current_page + 1
        },
        broadcastsViewSelectedBroadcastSet(state: State, broadcast: Broadcast): void {
            state.broadcastsViewSelectedBroadcast = broadcast
        },
        broadcastsViewSelectedGroupsSet(state: State, groups: Group[]): void {
            state.broadcastsViewSelectedGroups = groups
        },
        broadcastsViewSelectedBroadcastLoadingCountSet(state: State, bool: boolean): void {
            state.broadcastsViewSelectedBroadcastLoadingCount = bool
        },
        broadcastsViewSelectedBroadcastTotalCountSet(state: State, total: number): void {
            state.broadcastsViewSelectedBroadcastTotalCount = total
        },
        //  BROADCASTS TEMPLATES
        broadcastsViewSelectedTemplateSet(state: State, template: BroadcastTemplate): void {
            state.broadcastsViewSelectedTemplate = template
        },
        broadcastsViewMediaHintSet(state: State, hint: boolean): void {
            state.broadcastsViewMediaHint = hint
        },
        broadcastsViewReset(state: State): void {
            Object.assign(state, defaults())
        }
    },
    actions: {
        broadcastsViewBroadcastsPaginate({ commit, getters }): Promise<Pagination> {
            //  Select our pagination params
            const p = getters.broadcastsViewBroadcastsParams

            //  Start the paginating tracker
            commit('broadcastsViewPaginatingSet', true)

            //  Assemble the API endpoint
            const url = `${getters.api}/broadcasts?${window.toUrlParams(p)}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Update pagination params
                commit('broadcastsViewPaginationPush', response.data)

                //  Update the broadcasts broadcasts list
                commit('broadcastsViewBroadcastsPush', response.data.data)

                //  Stop the paginating tracker
                commit('broadcastsViewPaginatingSet', false)

                //  Return the pagination data
                return response.data
            }).catch(e => console.error("BroadcastsViewBroadcastsPaginate ERROR", e))
        },
        broadcastsViewSelectedBroadcastGet({ commit, getters }, broadcastId: number): Promise<Broadcast> {
            //  Grab the global broadcasts list
            const broadcasts = getters.broadcasts

            //  Check if this broadcast exists in the list
            const existingBroadcast = broadcasts.find((x: Broadcast) => x.id == broadcastId)

            //  If there IS an existing broadcast, commit that and proceed
            if (existingBroadcast) return commit('broadcastsViewSelectedBroadcastSet', existingBroadcast)

            //  Start the paginating tracker
            commit('broadcastsViewPaginatingSet', true)

            //  Else, assemble our API endpoint
            const url = `${getters.api}/broadcasts/${broadcastId}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set the broadcasts selected broadcast
                commit('broadcastsViewSelectedBroadcastSet', response.data)

                //  Push this into our global dataset
                commit('broadcastsPush', [response.data])

                //  Stop the paginating tracker
                commit('broadcastsViewPaginatingSet', false)

                //  REturn our broadcast
                return response.data
            }).catch(() => null)
        },
        broadcastsViewSelectedGroupsGet({ commit, getters }, ids: number[]): Promise<void> {
            //  Grab the global groups dataset
            const groups = getters.groups

            //  Check if we already have these groups
            const existingGroups = groups.filter((x: Group) => ids.includes(x.id))

            //  If there IS an existing group then commit it and proceed
            if (existingGroups.length) return commit('broadcastsViewSelectedGroupsSet', existingGroups)

            //  Create our payload
            const payload = { group_ids: ids }

            //  Assemble our API endpoint
            const url = `${getters.api}/groups/wherein`

            //  Send the request
            return window.axios.post(url, payload).then(response => {
                //  Set the selected group
                commit('broadcastsViewSelectedGroupsSet', response.data)

                //  Check if this group is a filter group
                // const filtered = response.data.query

                //  Add this group to our global dataset IF it isn't filter group
                //  We only want to add stuff to our global dataset if it has all fields already (in this case, the persons field)
                //  Eventually we will update this to also work with filtered groups if we manage to get all of their persons
                // if (!filtered) commit('groupsPush', [response.data])
            }).catch(() => null)
        },
        broadcastsViewSelectedBroadcastTotalCount({ commit, getters }, broadcast: Broadcast): Promise<number> {
            //  Assemble the API endpoint
            const url = `${getters.api}/broadcasts/persons`

            //  Start the loading
            commit('broadcastsViewSelectedBroadcastLoadingCountSet', true)

            //  Send the request
            return window.axios.post(url, broadcast).then(response => {
                //  Set our total number of persons in the store
                commit('broadcastsViewSelectedBroadcastTotalCountSet', response.data)

                //  Stop the loading
                commit('broadcastsViewSelectedBroadcastLoadingCountSet', false)

                //  Return our total number of persons
                return response.data
            }).catch(() => null)
        },
        broadcastsViewFailedMessagesGet({ commit, getters }, broadcastId: number): Promise<void> {
            //  Grab our pagination params
            const p = getters.broadcastsViewFailedMessagesParams

            //  Attach our broadcast id
            p.broadcast_id = broadcastId

            //  Start our pagination tracker
            commit('broadcastsViewFailedMessagesPaginatingSet', true)

            //  Assemble the API endpoint
            const url = `${getters.api}/broadcasts/failed?${window.toUrlParams(p)}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Update our pagination params
                commit('broadcastsViewFailedMessagesPaginationPush', response.data)

                //  Set the selected messages
                commit('broadcastsViewFailedMessagesPush', response.data.data)

                //  Stop our pagination tracker
                commit('broadcastsViewFailedMessagesPaginatingSet', false)
            }).catch(() => null)
        },
        broadcastsViewSentMessagesGet({ commit, getters }, broadcastId: number): Promise<void> {
            //  Grab our pagination params
            const p = getters.broadcastsViewSentMessagesParams

            //  Attach our broadcast id
            p.broadcast_id = broadcastId

            //  Start our pagination tracker
            commit('broadcastsViewSentMessagesPaginatingSet', true)

            //  Assemble the API endpoint
            const url = `${getters.api}/broadcasts/sent?${window.toUrlParams(p)}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Update our pagination params
                commit('broadcastsViewSentMessagesPaginationPush', response.data)

                //  Set the selected messages
                commit('broadcastsViewSentMessagesPush', response.data.data)

                //  Stop our pagination tracker
                commit('broadcastsViewSentMessagesPaginatingSet', false)
            }).catch(() => null)
        },
        broadcastsViewTotalSendingMessagesCount({ commit, getters }, broadcastId: number): Promise<number> {
            //  Assemble the API endpoint
            const url = `${getters.api}/broadcasts/total/${broadcastId}?sending=true`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Update our total
                commit('broadcastsViewTotalSendingMessagesSet', response.data)

                //  Return our data
                return response.data
            }).catch(() => null)
        },
        broadcastsViewTotalMessagesCount({ commit, getters }, broadcastId: number): Promise<number> {
            //  Assemble the API endpoint
            const url = `${getters.api}/broadcasts/total/${broadcastId}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Update our total
                commit('broadcastsViewTotalMessagesSet', response.data)

                //  Return our data
                return response.data
            }).catch(() => null)
        }
    },
    getters: {
        broadcastsViewBroadcasts(state: State): Broadcast[] {
            return state.broadcastsViewBroadcasts
        },
        broadcastsViewBroadcastsCurrentPage(state: State): number {
            return state.broadcastsViewBroadcastsCurrentPage
        },
        broadcastsViewBroadcastsLastPage(state: State): number {
            return state.broadcastsViewBroadcastsLastPage
        },
        broadcastsViewBroadcastsNextPage(state: State): number {
            return state.broadcastsViewBroadcastsNextPage
        },
        broadcastsViewBroadcastsParams(state: State): BroadcastParams {
            return state.broadcastsViewBroadcastsParams
        },
        broadcastsViewBroadcastsParamsDefault(state: State): BroadcastParams {
            return state.broadcastsViewBroadcastsParamsDefault
        },
        broadcastsViewGroups(state: State): Group[] {
            return state.broadcastsViewGroups
        },
        broadcastsViewFailedMessages(state: State): Message[] {
            return state.broadcastsViewFailedMessages
        },
        broadcastsViewFailedMessagesLastPage(state: State): number {
            return state.broadcastsViewFailedMessagesLastPage
        },
        broadcastsViewFailedMessagesNextPage(state: State): number {
            return state.broadcastsViewFailedMessagesNextPage
        },
        broadcastsViewFailedMessagesPaginating(state: State): boolean {
            return state.broadcastsViewFailedMessagesPaginating
        },
        broadcastsViewFailedMessagesParams(state: State): MessageParams {
            return state.broadcastsViewFailedMessagesParams
        },
        broadcastsViewFailedMessagesTotal(state: State): number {
            return state.broadcastsViewFailedMessagesTotal
        },
        broadcastsViewSentMessages(state: State): Message[] {
            return state.broadcastsViewSentMessages
        },
        broadcastsViewSentMessagesLastPage(state: State): number {
            return state.broadcastsViewSentMessagesLastPage
        },
        broadcastsViewSentMessagesNextPage(state: State): number {
            return state.broadcastsViewSentMessagesNextPage
        },
        broadcastsViewSentMessagesPaginating(state: State): boolean {
            return state.broadcastsViewSentMessagesPaginating
        },
        broadcastsViewSentMessagesParams(state: State): MessageParams {
            return state.broadcastsViewSentMessagesParams
        },
        broadcastsViewSentMessagesTotal(state: State): number {
            return state.broadcastsViewSentMessagesTotal
        },
        broadcastsViewTotalSendingMessages(state: State): number {
            return state.broadcastsViewTotalSendingMessages
        },
        broadcastsViewTotalMessages(state: State): number {
            return state.broadcastsViewTotalMessages
        },
        broadcastsViewPaginating(state: State): boolean {
            return state.broadcastsViewPaginating
        },
        broadcastsViewSelectedBroadcast(state: State): Broadcast {
            return state.broadcastsViewSelectedBroadcast
        },
        broadcastsViewSelectedGroups(state: State): Group[] {
            return state.broadcastsViewSelectedGroups
        },
        broadcastsViewSelectedBroadcastTotalCount(state: State): number {
            return state.broadcastsViewSelectedBroadcastTotalCount
        },
        broadcastsViewSelectedBroadcastLoadingCount(state: State): boolean {
            return state.broadcastsViewSelectedBroadcastLoadingCount
        },
        broadcastsViewSelectedTemplate(state: State): BroadcastTemplate {
            return state.broadcastsViewSelectedTemplate
        },
        broadcastsViewMediaHint(state: State): boolean {
            return state.broadcastsViewMediaHint
        }
    }
}